import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import { useInvoices } from '../../hooks/useInvoice';
import DriverInvoicesSection from '../../components/driver/DriverInvoicesSection';
import DriverFeeSection from '../../components/driver/DriverFeeSection';
import PayMethodBlock from '../../components/driver/PayMethodBlock';
import DriverTabBar from '../../components/driver/DriverTabBar';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckLoader } from '../../components/common/truck';
import { weekPayouts } from '../../utils/weeklyEarnings';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const STATUS_LABEL = {
  pending:    { text: 'Behandles',  bg: COLORS.warningBg, color: COLORS.warningText },
  authorized: { text: 'Autorisert', bg: COLORS.blueBg,    color: COLORS.blueText },
  captured:   { text: 'Betalt',     bg: COLORS.successBg, color: COLORS.successText },
  paid_out:   { text: 'Utbetalt',   bg: COLORS.successBg, color: COLORS.successText },
  failed:     { text: 'Feilet',     bg: COLORS.dangerBg,  color: COLORS.dangerText },
};

export default function Earnings() {
  const navigate = useNavigate();
  const { invoices, loading } = useInvoices();

  const isMobile = useIsMobile();
  const totalPayout   = invoices.reduce((s, inv) => s + (inv.driverPayout  ?? 0), 0);
  const totalTurnover = invoices.reduce((s, inv) => s + (inv.totalAmount   ?? 0), 0);

  const week = weekPayouts(invoices);

  return (
    <div className="driver-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>

        {!isMobile && (
          <button style={styles.backBtn} onClick={() => navigate('/driver')}>
            ← Tilbake til kart
          </button>
        )}

        <h1 style={styles.pageTitle}>Inntekt</h1>

        {/* Denne uken */}
        <section style={styles.hero} aria-label="Denne uken">
          <div style={styles.heroLabel}>Denne uken</div>
          <div style={styles.heroAmount}>kr {week.total.toLocaleString('nb-NO')}</div>
          <div style={styles.bars}>
            {week.days.map(d => (
              <div key={d.label} style={styles.barCol}>
                <div style={{
                  ...styles.bar,
                  height: d.amount > 0 ? Math.max(8, Math.round(d.amount / week.max * 72)) : 3,
                  background: d.amount > 0 ? COLORS.accent : 'rgba(255,255,255,0.22)',
                }} title={`kr ${d.amount.toLocaleString('nb-NO')}`} />
                <span style={styles.barLabel}>{d.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Totalt, siden start */}
        <div style={styles.kpis}>
          <Kpi value={invoices.length} label="Fullførte oppdrag" />
          <Kpi value={totalTurnover.toLocaleString('nb-NO')} label="Total omsetning (kr)" />
          <Kpi dark value={totalPayout.toLocaleString('nb-NO')} label="Din nettoinntekt (kr)" />
        </div>

        {/* Slik får du betalt: faktura som standard, Stripe nedtonet */}
        <PayMethodBlock hasCardEarnings={invoices.some(inv => inv.status === 'captured')} />

        <DriverInvoicesSection />
        <DriverFeeSection />

        {/* Fakturahistorikk */}
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Oppdragshistorikk</h2>

          {loading && <TruckLoader showLabel label="Laster…" size={64} />}

          {!loading && invoices.length === 0 && (
            <div style={styles.empty}>
              <div style={{ fontSize: 36 }}>💰</div>
              <p style={{ marginTop: 12 }}>Ingen fullførte oppdrag ennå</p>
            </div>
          )}

          {invoices.map(inv => (
            <InvoiceRow key={inv.id} invoice={inv} />
          ))}
        </div>
      </div>
      <DriverTabBar />
    </div>
  );
}

function Kpi({ value, label, dark }) {
  return (
    <div style={{ ...styles.kpi, ...(dark ? styles.kpiDark : {}) }}>
      <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: String(value).length > 5 ? 21 : 28, lineHeight: 1.1, color: dark ? COLORS.accent : COLORS.textDark }}>{value}</div>
      <div style={{ fontSize: 10.5, lineHeight: 1.2, marginTop: 3, color: dark ? 'rgba(255,255,255,0.7)' : COLORS.textLight }}>{label}</div>
    </div>
  );
}

function InvoiceRow({ invoice }) {
  const statusInfo = STATUS_LABEL[invoice.status] ?? { text: invoice.status, bg: COLORS.surfaceHover, color: COLORS.textLight };
  const date = invoice.createdAt?.toDate?.();

  return (
    <div style={styles.invoiceRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Oppdrag #{invoice.orderId?.slice(-6)}</div>
        <div style={styles.mono}>
          {date ? date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).toUpperCase() : ''}
        </div>
        <div style={{ fontSize: 12.5, color: COLORS.textMid, marginTop: 3 }}>
          Grunnbeløp kr {invoice.baseAmount}
          {invoice.adjustmentAmount !== 0 && <> · Justering {invoice.adjustmentAmount > 0 ? '+' : '−'}kr {Math.abs(invoice.adjustmentAmount)}</>}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 24, lineHeight: 1 }}>
          {invoice.driverPayout?.toLocaleString('nb-NO')}
        </div>
        <span style={{ ...styles.badge, background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.text}</span>
      </div>
    </div>
  );
}

const styles = {
  root:        { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:        { maxWidth: 720, margin: '0 auto', padding: '24px 16px', width: '100%', flex: 1, paddingTop: 'max(32px, env(safe-area-inset-top))' },
  backBtn:     { background: 'none', border: 'none', color: '#555', fontSize: 14, cursor: 'pointer', padding: '0 0 12px', display: 'block' },
  pageTitle:   { fontFamily: FONTS.display, fontSize: 36, lineHeight: 1, fontWeight: 700, color: COLORS.textDark, margin: '0 0 16px' },
  hero:        { background: COLORS.heroBg, border: `1px solid ${COLORS.heroBorder}`, color: '#fff', borderRadius: 24, padding: '20px', marginBottom: 14 },
  heroLabel:   { fontSize: 13, color: 'rgba(255,255,255,0.7)' },
  heroAmount:  { fontFamily: FONTS.display, fontSize: 56, lineHeight: 1, fontWeight: 700, color: COLORS.accent, margin: '8px 0 16px' },
  bars:        { display: 'flex', gap: 10, alignItems: 'flex-end', height: 96 },
  barCol:      { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6, height: '100%' },
  bar:         { width: '100%', borderRadius: '5px 5px 2px 2px' },
  barLabel:    { fontSize: 11, color: 'rgba(255,255,255,0.7)' },
  kpis:        { display: 'flex', gap: 8, marginBottom: 14 },
  kpi:         { flex: 1, minWidth: 0, background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '10px 10px 9px' },
  kpiDark:     { background: COLORS.brandPanel, border: 'none' },
  mono:        { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 2, letterSpacing: '0.03em' },
  badge:       { display: 'inline-block', borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, marginTop: 5 },
  card:        { background: 'transparent', padding: '4px 0 0', marginBottom: 16 },
  sectionTitle:{ fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: COLORS.textDark, margin: '4px 0 2px' },
  invoiceRow:  { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` },
  empty:       { textAlign: 'center', padding: '24px', color: '#888' },
};
